/**
 * Validation Utilities
 *
 * Field validation functions for forms and data entry.
 */

import { type CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import type { FieldDefinition, ValidationRule } from '../types/entityFramework';

// ============================================================================
// Validation Result Types
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

// ============================================================================
// Basic Validators
// ============================================================================

/**
 * Check if a value is empty
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Check if a value is a valid email
 */
export function isValidEmail(value: string | null | undefined): boolean {
  if (!value) return false;
  return EMAIL_REGEX.test(value);
}

/**
 * Check if a value is a valid URL
 */
export function isValidUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if a value is a valid phone number
 */
export function isValidPhone(value: string | null | undefined, country: CountryCode = 'US'): boolean {
  if (!value) return false;
  try {
    return isValidPhoneNumber(value, country);
  } catch {
    return false;
  }
}

/**
 * Check if a value is a valid number
 */
export function isValidNumber(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  return Number.isFinite(Number(value));
}

/**
 * Check if a value is within a numeric range
 */
export function isInRange(value: number, min?: number, max?: number): boolean {
  if (min !== undefined && value < min) return false;
  if (max !== undefined && value > max) return false;
  return true;
}

/**
 * Check string length
 */
export function isValidLength(value: string | null | undefined, minLength?: number, maxLength?: number): boolean {
  if (!value) return minLength === undefined || minLength === 0;
  if (minLength !== undefined && value.length < minLength) return false;
  if (maxLength !== undefined && value.length > maxLength) return false;
  return true;
}

/**
 * Check if a value matches a regex pattern
 */
export function matchesPattern(value: string | null | undefined, pattern: string | RegExp): boolean {
  if (!value) return false;
  const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
  return regex.test(value);
}

// ============================================================================
// Field-Type Specific Validators
// ============================================================================

/**
 * Validate a currency value
 */
export function isValidCurrency(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  const num = Number(value);
  return Number.isFinite(num) && num >= 0;
}

/**
 * Validate a percentage value (0-100)
 */
export function isValidPercentage(value: unknown): boolean {
  if (!isValidNumber(value)) return false;
  const num = Number(value);
  return num >= 0 && num <= 100;
}

/**
 * Validate a date value
 */
export function isValidDate(value: unknown): boolean {
  if (!value) return false;
  const date = value instanceof Date ? value : new Date(value as string);
  return !Number.isNaN(date.getTime());
}

/**
 * Validate a JSON string
 */
export function isValidJSON(value: string | null | undefined): boolean {
  if (!value) return false;
  try {
    JSON.parse(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate an SSN (US Social Security Number)
 */
export function isValidSSN(value: string | null | undefined): boolean {
  if (!value) return false;
  const cleaned = value.replace(/\D/g, '');
  return cleaned.length === 9;
}

/**
 * Validate an EIN (US Employer Identification Number)
 */
export function isValidEIN(value: string | null | undefined): boolean {
  if (!value) return false;
  const cleaned = value.replace(/\D/g, '');
  return cleaned.length === 9;
}

/**
 * Validate a file type
 */
export function isValidFileType(file: File, acceptedTypes: string[]): boolean {
  if (!file || !acceptedTypes?.length) return true;

  const fileType = file.type;
  const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

  return acceptedTypes.some((type) => {
    if (type.startsWith('.')) {
      return fileExtension === type.toLowerCase();
    }
    if (type.endsWith('/*')) {
      const prefix = type.slice(0, -2);
      return fileType.startsWith(prefix);
    }
    return fileType === type;
  });
}

/**
 * Validate file size
 */
export function isValidFileSize(file: File, maxSize: number): boolean {
  return file.size <= maxSize;
}

// ============================================================================
// Field Validation
// ============================================================================

/**
 * Validate a single validation rule
 */
export function validateRule(value: unknown, rule: ValidationRule): string | null {
  switch (rule.type) {
    case 'required':
      if (isEmpty(value)) return rule.message;
      break;

    case 'min':
      if (typeof value === 'number' && value < (rule.value as number)) {
        return rule.message;
      }
      break;

    case 'max':
      if (typeof value === 'number' && value > (rule.value as number)) {
        return rule.message;
      }
      break;

    case 'minLength':
      if (typeof value === 'string' && value.length < (rule.value as number)) {
        return rule.message;
      }
      break;

    case 'maxLength':
      if (typeof value === 'string' && value.length > (rule.value as number)) {
        return rule.message;
      }
      break;

    case 'pattern':
      if (typeof value === 'string' && !matchesPattern(value, rule.value as string)) {
        return rule.message;
      }
      break;

    case 'custom':
      // Custom validation would need a function, but we just use the message
      break;
  }

  return null;
}

/**
 * Validate a field value against its definition
 */
export function validateField(value: unknown, field: FieldDefinition): ValidationResult {
  const errors: string[] = [];

  // Required check
  if (field.required && isEmpty(value)) {
    errors.push(`${field.label} is required`);
    return { isValid: false, errors };
  }

  // Skip further validation if empty and not required
  if (isEmpty(value)) {
    return { isValid: true, errors: [] };
  }

  // Type-specific validation
  switch (field.fieldType) {
    case 'EMAIL':
      if (!isValidEmail(value as string)) {
        errors.push('Please enter a valid email address');
      }
      break;

    case 'PHONE':
      if (!isValidPhone(value as string)) {
        errors.push('Please enter a valid phone number');
      }
      break;

    case 'URL':
      if (!isValidUrl(value as string)) {
        errors.push('Please enter a valid URL');
      }
      break;

    case 'NUMBER':
    case 'CURRENCY':
      if (!isValidNumber(value)) {
        errors.push('Please enter a valid number');
      } else {
        const num = Number(value);
        if (field.min !== undefined && num < field.min) {
          errors.push(`Value must be at least ${field.min}`);
        }
        if (field.max !== undefined && num > field.max) {
          errors.push(`Value must be at most ${field.max}`);
        }
      }
      break;

    case 'PERCENTAGE':
      if (!isValidPercentage(value)) {
        errors.push('Please enter a valid percentage (0-100)');
      }
      break;

    case 'DATE':
    case 'DATETIME':
    case 'TIME':
      if (!isValidDate(value)) {
        errors.push('Please enter a valid date');
      }
      break;

    case 'JSON':
      if (!isValidJSON(value as string)) {
        errors.push('Please enter valid JSON');
      }
      break;

    case 'SELECT':
    case 'MULTI_SELECT':
      if (field.options) {
        const validValues = field.options.map((o) => o.value);
        const values = Array.isArray(value) ? value : [value];
        if (values.some((v) => !validValues.includes(v as string))) {
          errors.push('Please select a valid option');
        }
      }
      break;
  }

  // Custom validation rules
  if (field.validation) {
    for (const rule of field.validation) {
      const error = validateRule(value, rule);
      if (error) {
        errors.push(error);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate multiple fields
 */
export function validateFields(
  values: Record<string, unknown>,
  fields: FieldDefinition[],
): Record<string, ValidationResult> {
  return Object.fromEntries(fields.map((field) => [field.name, validateField(values[field.name], field)]));
}

/**
 * Check if all field validations passed
 */
export function isFormValid(validationResults: Record<string, ValidationResult>): boolean {
  return Object.values(validationResults).every((result) => result.isValid);
}

/**
 * Get first error from validation results
 */
export function getFirstError(validationResults: Record<string, ValidationResult>): string | null {
  for (const result of Object.values(validationResults)) {
    if (!result.isValid && result.errors.length > 0) {
      return result.errors[0];
    }
  }
  return null;
}

/**
 * Get all errors as a flat array
 */
export function getAllErrors(validationResults: Record<string, ValidationResult>): string[] {
  return Object.values(validationResults).flatMap((result) => result.errors);
}
